<template>
    <div>
        <translation-string :translation="$t('jurisdictionRequirements.ukSelfCategorisation.sophisticatedInvestorTitlePage')"
            class="title"
        />

        <translation-string :translation="$t('jurisdictionRequirements.ukSelfCategorisation.sophisticatedInvestorText1')"
            class="message"
        />

        <div class="options">
            <div v-for="(option,index) in sophisticatedHasCertificateOptions"
                :key="index"
                class="radio__wrapper"
                @click="toggleSophisticatedHasCertificateOption(index)"
            >
                <div class="radio">
                    <div
                        class="radio__circle"
                    />
                    <div v-if="option.checked"
                        class="radio__circle--fill"
                    />
                </div>
                <label class="radio__label"
                    :for="index"
                >
                    <translation-string
                        class="uk-investor-statement__message"
                        :translation="option.text"
                    />
                </label>
            </div>
        </div>

        <custom-input ref="sophisticatedFirmInput"
            v-model.trim="sophisticatedFirmName"
            :disabled="!sophisticatedHasCertificate"
            :label="$t('jurisdictionRequirements.ukSelfCategorisation.authorisedFirm')"
            :name="$t('jurisdictionRequirements.ukSelfCategorisation.authorisedFirm')"
            :placeholder="$t('jurisdictionRequirements.ukSelfCategorisation.authorisedFirmPlaceholder')"
            @input="validateSteps"
        />

        <label :class="['upload', {'upload--disabled': !sophisticatedHasCertificate}]">
            <div class="upload__image">
                <img src="@webapp/assets/upload-document.webp"
                    alt="upload"
                >
            </div>
            <div class="upload__text">
                <h2 class="upload__title">{{ $t('kycUploadMethods.uploadDocumentTitle') }}</h2>
                <p class="upload__description">
                    {{ $t('jurisdictionRequirements.ukSelfCategorisation.uploadCertificate') }}
                </p>
            </div>
            <input
                type="file"
                multiple
                accept="image/*"
                class="hidden"
                :disabled="!sophisticatedHasCertificate"
                @change="onFilePicked($event)"
            >
        </label>

        <alert-panel
            v-if="imageToUpload && isAlertVisible"
            type="success"
            :title="$t('jurisdictionRequirements.ukSelfCategorisation.fileUploaded')"
            :description="$t('jurisdictionRequirements.ukSelfCategorisation.fileUploadedDescription')"
            icon="check"
            class="upload-alert"
            noSideMargin
            @onClose="closeAlert"
        />

        <translation-string class="message"
            :translation="$t('jurisdictionRequirements.ukSelfCategorisation.or')"
        />
        <translation-string class="message"
            :translation="$t('jurisdictionRequirements.ukSelfCategorisation.doesNotApply')"
        />


        <div class="options">
            <div v-for="(option,index) in resetAndSetToFalseOption"
                :key="index"
                class="radio__wrapper"
                @click="resetAndSetToFalse(index)"
            >
                <div class="radio">
                    <div
                        class="radio__circle"
                    />
                    <div v-if="option.checked"
                        class="radio__circle--fill"
                    />
                </div>
                <label class="radio__label"
                    :for="index"
                >
                    <translation-string
                        class="uk-investor-statement__message"
                        :translation="option.text"
                    />
                </label>
            </div>
        </div>

        <user-infos
            v-model="userInfo"
            :requirement="requirement"
            @updateFirstName="onUserInfoUpdate('firstName', $event)"
            @updateLastName="onUserInfoUpdate('lastName', $event)"
        />
    </div>
</template>

<style scoped lang="scss">
.title {
    font-size: 2.5rem;
    color: #002e3c;
    padding: 0 2rem;
    text-align: center;
    display: block;
    font-weight: bold;
    margin-bottom: 30px;
}

.message {
    display: block;
    font-size: 1.65rem;
    line-height: 28px;
    margin: 10px 0;
}

.upload {
    padding: 15px 30px;
    border-radius: 8px;
    border: 1px solid $black-color;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    cursor: pointer;

    & img {
        width: 50px;
    }

    &--disabled {
        opacity: 0.7;
        pointer-events: none;
    }

    &__title {
        font-size: 1.6rem;
        font-weight: 700;
    }

    &__description {
        font-size: 14px;
    }
}

.options {
    & .radio {
        position: relative;

        &__wrapper {
            display: flex;
            margin: 20px 0;
            cursor: pointer;
        }

        &__circle {
            height: 15px;
            width: 15px;
            margin-right: 10px;
            border: 1px solid $lima-color;
            border-radius: 50%;

            &--fill {
                height: 11px;
                width: 11px;
                background-color: $lima-color;
                border-radius: 50%;
                position: absolute;
                top: 3px;
                left: 3px;
            }
        }

        &__label {
            font-size: 1.65rem;
            margin-top: -2px;
            cursor: pointer;
        }
    }
}

.hidden {
    visibility: hidden;
}
</style>

<script>
// Components
import customInput from '@webapp/components/form/custom-input.vue';
import userInfos from '@webapp/modules/customer-first/components/requirements/uk-self-categorisation-assessment/user-infos.vue';
import translationString from '@webapp/components/translation-string.vue';
import AlertPanel from '@webapp/components/alert/alert-panel.vue';


export default {
    name: 'sophisticated-investor-form',
    components: {
        customInput,
        userInfos,
        translationString,
        AlertPanel,
    },
    props: {
        requirement: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            sophisticatedHasCertificate: null,
            sophisticatedFirmName: null,
            imageToUpload: null,
            userInfo: {
                firstName: '',
                lastName: '',
            },
            sophisticatedHasCertificateOptions: [
                {
                    checked: false,
                    text: this.$t('jurisdictionRequirements.ukSelfCategorisation.optionNo'),
                    value: false,
                },
                {
                    checked: false,
                    text: this.$t('jurisdictionRequirements.ukSelfCategorisation.sophisticatedHasCertificateOptionsYes'),
                    value: true,
                },
            ],
            resetAndSetToFalseOption: [
                {
                    checked: false,
                    text: this.$t('jurisdictionRequirements.ukSelfCategorisation.optionYes'),
                    value: false,
                },
            ],
            isAlertVisible: false,
        };
    },
    computed: {
        selectedSophisticatedHasCertificateOption() {
            return this.sophisticatedHasCertificateOptions.find((option) => option.checked);
        },
        isFormValid() {
            const certificateCondition =
            this.sophisticatedHasCertificate === false ||
            (this.sophisticatedHasCertificate === true && this.sophisticatedFirmName !== null && this.imageToUpload !== null);

            return certificateCondition && this.userInfo.firstName !== '' && this.userInfo.lastName !== '';
        },
    },
    watch: {
        imageToUpload: {
            handler: 'validateSteps',
            immediate: true,
        },
    },
    methods: {
        updateSophisticatedHasCertificateOptionByIndex(index) {
            this.resetAndSetToFalseOption[0].checked = false;
            this.sophisticatedHasCertificateOptions.forEach((opt) => {
                opt.checked = false;
            });

            this.$refs.sophisticatedFirmInput.reset();

            this.sophisticatedHasCertificateOptions[index].checked = true;
            this.sophisticatedHasCertificate = this.selectedSophisticatedHasCertificateOption.value;

            this.validateSteps();
        },
        toggleSophisticatedHasCertificateOption(index) {
            this.updateSophisticatedHasCertificateOptionByIndex(index);
        },
        onUserInfoUpdate(field, value) {
            this.userInfo[field] = value;

            this.validateSteps();
        },
        resetAndSetToFalse(index) {
            this.resetAndSetToFalseOption.forEach((opt) => {
                opt.checked = false;
            });
            this.sophisticatedHasCertificateOptions.forEach((opt) => {
                opt.checked = false;
            });

            this.sophisticatedHasCertificate = false;
            this.sophisticatedFirmName = null;
            this.imageToUpload = null;

            this.resetAndSetToFalseOption[index].checked = true;

            this.validateSteps();
        },
        onFilePicked(e) {
            const file = e.target.files[0];

            if (!file) {
                return;
            }

            const image = {};
            const fr = new FileReader();

            image.name = file.name;

            if (image.name.lastIndexOf('.') <= 0) {
                return;
            }

            image.src = URL.createObjectURL(file);
            image.mimeType = file.name.split('.').pop();
            image.format = 'selfie';

            fr.readAsDataURL(file);

            fr.addEventListener('load', () => {
                image.content = fr.result.split(',').pop();
                this.imageToUpload = image;
            });
            this.isAlertVisible = true;
            this.validateSteps();
        },
        async validateSteps() {
            await this.$nextTick();

            if (this.isFormValid) {
                this.$emit('completed', {
                    firstName: this.userInfo.firstName,
                    lastName: this.userInfo.lastName,
                    category: 'certified_sophisticated',
                    sophisticatedHasCertificate: this.sophisticatedHasCertificate,
                    sophisticatedFirmName: this.sophisticatedFirmName,
                    sophisticatedCertifiedDocument: this.imageToUpload ? this.imageToUpload.content : null,
                });
            } else {
                this.$emit('in-progress');
            }
        },
        closeAlert() {
            this.isAlertVisible = false;
        },
    },
};
</script>